import { useEffect, useId, useMemo, useState } from 'react'
import { catalogRowIsOwned } from '../../lib/catalogSearchHierarchy'
import { lineKeySignature, liveLineKey, rowsForLiveLine } from '../../lib/catalogSearchLiveLines'
import { lineRepIsPlaceholder, resolveCatalogSearchPresentation } from '../../lib/catalogSearchHouseFirst'
import { resolvedConcentrationDisplay } from '../../lib/concentrationResolver'
import { BottleThumbnail } from '../collection/HierarchyMultiAddModal'
import WishListAction from '../wishlist/WishListAction'
import GroupedSearchResults from './GroupedSearchResults'

/**
 * Catalog search results grouped by fragrance name (FOS-SEARCH-LINE-GROUPING-001).
 *
 * Takes the same props as `CatalogSearchHierarchy`, which renders this
 * component when the `name_grouped_search` flag is on. The path is
 * house → name → concentration: "Sauvage" is one row with EDT, EDP and Parfum
 * beneath it, while "Sauvage Elixir" keeps its own row.
 *
 * The line-only search path is preserved: when the search service answers
 * with lines (`fos_search_lines_v1`) and no bottle rows, each line renders as
 * a row that loads its bottles on open through `loadLineVariants`, and those
 * bottles are then grouped by name.
 *
 * @param {string} query - Current search query
 * @param {Array} rows - Catalog search rows (`results` is accepted as an alias)
 * @param {Array} [lines] - Live line hits from the search service
 * @param {Function} [loadLineVariants] - `(house, lineKey) => Promise<rows>`
 * @param {Array} [collection] - Owner's collection for ownership marks
 * @param {Function} [findDuplicate] - Alternative ownership check
 * @param {Function} onSelect - Called with the chosen catalog row
 * @param {boolean} [disabled] - Whether selection is disabled
 */
export default function CatalogSearchGrouped({
  query = '',
  rows,
  results,
  lines = [],
  loadLineVariants,
  collection = [],
  findDuplicate,
  onSelect,
  disabled = false,
}) {
  const sourceRows = rows || results || []
  const presentation = useMemo(
    () => resolveCatalogSearchPresentation(query, sourceRows, lines),
    [query, sourceRows, lines],
  )
  const hasLiveLines = presentation.useLiveLines
  const liveSignature = useMemo(() => lineKeySignature(presentation.displayLines), [presentation.displayLines])
  const [activeLine, setActiveLine] = useState(null)
  const [liveVariants, setLiveVariants] = useState([])
  const [liveLoading, setLiveLoading] = useState(false)
  const instance = useId()

  const owned = row => collection.length
    ? catalogRowIsOwned(row, collection)
    : Boolean(findDuplicate?.({ ...row, concentration: resolvedConcentrationDisplay(row) }))
  const wishAction = (row, isOwned) => (
    <WishListAction catalogId={row.id} owned={isOwned || disabled} source="search" />
  )

  useEffect(() => {
    setActiveLine(null)
    setLiveVariants([])
  }, [sourceRows, lines, query])

  useEffect(() => {
    if (!hasLiveLines || !activeLine) {
      setLiveVariants([])
      setLiveLoading(false)
      return undefined
    }
    const line = presentation.displayLines.find(entry => liveLineKey(entry) === activeLine)
    if (!line) return undefined
    let cancelled = false
    setLiveLoading(true)
    const apply = (nextRows) => {
      if (cancelled) return
      setLiveVariants(Array.isArray(nextRows) ? nextRows : [])
      setLiveLoading(false)
    }
    if (typeof loadLineVariants === 'function') {
      Promise.resolve(loadLineVariants(line.house, line.line_key))
        .then((nextRows) => apply(nextRows?.length ? nextRows : rowsForLiveLine(sourceRows, line)))
        .catch(() => apply(rowsForLiveLine(sourceRows, line)))
    } else {
      apply(rowsForLiveLine(sourceRows, line))
    }
    return () => { cancelled = true }
  }, [activeLine, hasLiveLines, liveSignature, loadLineVariants, sourceRows, presentation.displayLines])

  // Derived path: the house-first presentation already applied the house
  // lead and image demotion. Flatten each house's lines back to bottle rows
  // so the name grouping, not the line, decides what sits together.
  const visibleHouses = useMemo(
    () => (hasLiveLines ? [] : presentation.houses)
      .map(house => {
        const seen = new Set()
        const houseRows = (house.lines || [])
          .flatMap(line => line.variants || [])
          .filter(row => {
            if (!row?.id || seen.has(row.id)) return false
            seen.add(row.id)
            return true
          })
        return { house: house.house, rows: houseRows }
      })
      .filter(({ rows: houseRows }) => houseRows.length > 0),
    [hasLiveLines, presentation.houses],
  )

  return (
    <div
      data-catalog-search-grouped
      data-search-path="house-name-concentration"
      data-search-lead={presentation.lead}
      data-matched-house={presentation.house || undefined}
      data-search-line-source={hasLiveLines ? 'fos_search_lines_v1' : 'derived'}
    >
      {!hasLiveLines && visibleHouses.map(({ house, rows: houseRows }) => (
        <section key={house} aria-label={house} data-search-step="house">
          <h3 className="px-3 py-2 text-xs uppercase tracking-wider text-gold">{house}</h3>
          <div className="border-b border-white/5">
            <GroupedSearchResults
              rows={houseRows}
              query={query}
              onSelect={onSelect}
              isOwned={owned}
              disabled={disabled}
              showHouse={false}
              renderAction={wishAction}
              emptyMessage={null}
            />
          </div>
        </section>
      ))}

      {hasLiveLines && presentation.houses.map(house => (
        <section key={house.house} aria-label={house.house} data-search-step="house">
          <h3 className="px-3 py-2 text-xs uppercase tracking-wider text-gold">{house.house}</h3>
          {house.lines.map((line, index) => {
            const key = liveLineKey(line)
            const expanded = activeLine === key
            const panelId = `${instance}-${house.house}-${index}`
            const label = line.line_label || line.line_key
            const variantCount = Number(line.variant_count) || 0
            const thumbRow = {
              id: line.rep_id,
              name: label,
              house: line.house,
              card_image: line.rep_card_image,
              image_flagged: line.image_flagged,
            }
            return (
              <div key={key} className="border-b border-white/5" data-line-entry>
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  disabled={disabled}
                  onClick={() => setActiveLine(expanded ? null : key)}
                  className="flex min-h-16 w-full items-center gap-3 px-3 py-2 text-left hover:bg-white/5 focus:bg-white/5 disabled:opacity-50"
                  data-line-row
                  data-search-step="line"
                  data-line-key={line.line_key}
                  data-line-placeholder={lineRepIsPlaceholder(line) ? 'true' : 'false'}
                >
                  <BottleThumbnail row={thumbRow} size="line" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-cream">{label}</span>
                    {variantCount > 0 && (
                      <span className="text-[11px] text-cream/40">
                        {variantCount} {variantCount === 1 ? 'bottle' : 'bottles'}
                      </span>
                    )}
                  </span>
                  <span aria-hidden="true" className="text-gold">{expanded ? '−' : '+'}</span>
                </button>
                {expanded && (
                  <div
                    id={panelId}
                    role="group"
                    aria-label={`${label} fragrances`}
                    className="px-2 pb-2"
                    data-search-step="fragrance-list"
                  >
                    {liveLoading && (
                      <p className="px-2 py-2 text-xs text-cream/45">Loading bottles in this line…</p>
                    )}
                    {!liveLoading && (
                      <GroupedSearchResults
                        rows={liveVariants}
                        query={query}
                        onSelect={onSelect}
                        isOwned={owned}
                        disabled={disabled}
                        showHouse={false}
                        renderAction={wishAction}
                        emptyMessage="No bottles found in this line yet."
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </section>
      ))}
    </div>
  )
}
